import { catalogIssuesLegacyCanMatch } from './shared/store/catalog-issues/catalog-issues-legacy.guard';
// src/app/app.routes.ts
import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes } from '@angular/router';

import { authCanMatch } from './shared/store/auth/auth.guard';
import { guestOnlyCanMatch } from './shared/store/auth/guest-only.guard';
import { rbacAnyCanMatch, rbacCanMatch } from './shared/store/auth/rbac.guard';

const inventoryLabelListWarehouseGuard: CanActivateFn = (route) => {
  const warehouseId = (route.queryParamMap.get('warehouse_id') ?? '').trim();
  if (warehouseId) return true;

  const catalogItemId = (route.paramMap.get('catalogItemId') ?? '').trim();
  const router = inject(Router);

  return catalogItemId
    ? router.createUrlTree(['/app/catalog/product', catalogItemId])
    : router.createUrlTree(['/app/inventory-review']);
};

/**
 * Routes
 * -----------------------------------------------------------------------------
 * Минимальный, но “правильный” набор guard’ов для закрытой системы:
 *
 * 1) guestOnlyCanMatch — только для страницы логина
 *    - если уже авторизован => /app
 *    - invitation/reset-token сценарии должны оставаться публичными,
 *      даже если в браузере уже есть чужая активная сессия
 *
 * 2) authCanMatch — для /app/*
 *    - если guest => /auth/login
 *    - иначе пускаем (unknown допускаем, чтобы не было миганий при hydrate)
 *
 * RBAC (права) здесь НЕ вешаем глобально:
 * - права лучше проверять точечно (на конкретные фичи) через отдельный permission guard
 * - иначе получится “завалить всё приложение” из-за одной фичи.
 */
export const routes: Routes = [
  // ---------------------------------------------------------------------------
  // Root redirect
  // ---------------------------------------------------------------------------
  {
    path: '',
    redirectTo: 'app',
    pathMatch: 'full',
  },

  // ---------------------------------------------------------------------------
  // Public area: auth pages ONLY (+ anti-login)
  // ---------------------------------------------------------------------------
  {
    path: 'auth',
    loadComponent: () => import('./pages/auth/auth').then((m) => m.Auth),
    title: 'Вход',
    children: [
      { path: '', redirectTo: 'login', pathMatch: 'full' },

      {
        path: 'login',
        canMatch: [guestOnlyCanMatch],
        loadComponent: () => import('./pages/auth/login/login').then((m) => m.Login),
        title: 'Вход',
      },
      {
        path: 'password-reset',
        loadComponent: () => import('./pages/auth/password-reset/password-reset').then((m) => m.PasswordReset),
        title: 'Восстановление пароля',
      },
      {
        path: 'forgot-password',
        redirectTo: 'password-reset',
        pathMatch: 'full',
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./pages/auth/password-access/password-access').then((m) => m.PasswordAccess),
        title: 'Новый пароль',
        data: { mode: 'reset' },
      },
      {
        path: 'invitation',
        loadComponent: () => import('./pages/auth/password-access/password-access').then((m) => m.PasswordAccess),
        title: 'Приглашение',
        data: { mode: 'invitation' },
      },

      // 404 внутри /auth/*
      {
        path: '**',
        loadComponent: () => import('./pages/not-found/not-found').then((m) => m.NotFound),
        title: 'Не найдено'
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Protected area: app
  // ---------------------------------------------------------------------------
  {
    path: 'app',
    loadComponent: () => import('./pages/main/main').then((m) => m.Main),
    canMatch: [authCanMatch],
    children: [
      { path: '', redirectTo: 'catalog', pathMatch: 'full' },

      {
        path: 'supplies',
        canMatch: [rbacAnyCanMatch(['inventory.verify', 'inventory.review.approve'])],
        loadComponent: () => import('./pages/supply-acceptance-list/supply-acceptance-list').then((m) => m.SupplyAcceptanceList),
      },
      {
        path: 'supplies/:id',
        canMatch: [rbacAnyCanMatch(['inventory.verify', 'inventory.review.approve'])],
        loadComponent: () => import('./pages/supply-acceptance-detail/supply-acceptance-detail').then((m) => m.SupplyAcceptanceDetail),
      },
      {
        path: 'registry',
        canMatch: [rbacCanMatch('catalog.view')],
        loadComponent: () => import('./pages/registry/registry').then((m) => m.Registry),
        title: 'Реестры',
      },
      {
        path: 'supplier-counterparties',
        canMatch: [rbacCanMatch('supplier.counterparty.view')],
        loadComponent: () =>
          import('./pages/supplier-counterparties/supplier-counterparties').then((m) => m.SupplierCounterparties),
        title: 'Поставщики',
      },
      {
        path: 'supplier-invoices',
        canMatch: [rbacCanMatch('supplier.invoice.view')],
        loadComponent: () => import('./pages/supplier-invoices/supplier-invoices').then((m) => m.SupplierInvoices),
        title: 'Счета от поставщиков',
      },
      {
        path: 'supplier-invoices/:id',
        canMatch: [rbacCanMatch('supplier.invoice.view')],
        loadComponent: () =>
          import('./pages/supplier-invoice-detail/supplier-invoice-detail').then((m) => m.SupplierInvoiceDetail),
        title: 'Счет от поставщика',
      },
      {
        path: 'purchase-receipts',
        canMatch: [rbacCanMatch('purchase.receipt.view')],
        loadComponent: () =>
          import('./pages/purchase-receipts/purchase-receipts').then((m) => m.PurchaseReceipts),
        title: 'Поступления',
      },
      {
        path: 'purchase-receipts/:id',
        canMatch: [rbacCanMatch('purchase.receipt.view')],
        loadComponent: () =>
          import('./pages/purchase-receipt-detail/purchase-receipt-detail').then((m) => m.PurchaseReceiptDetail),
        title: 'Поступление',
      },
      {
        path: 'production-documents',
        canMatch: [rbacCanMatch('production.document.view')],
        loadComponent: () => import('./pages/production-documents/production-documents').then((m) => m.ProductionDocuments),
        title: 'Выпуск/комплектация',
      },
      {
        path: 'production-documents/:id',
        canMatch: [rbacCanMatch('production.document.view')],
        loadComponent: () => import('./pages/production-document-detail/production-document-detail').then((m) => m.ProductionDocumentDetail),
        title: 'Документ выпуска/комплектации',
      },

      {
        path: 'catalog',
        loadChildren: () => import('./app.catalog.routes').then((m) => m.CATALOG_ROUTES),
      },
      {
        path: 'profile',
        loadComponent: () => import('./pages/profile/profile').then((m) => m.Profile),
        title: 'Профиль',
      },

      {
        path: 'users',
        canMatch: [rbacCanMatch('user.view')],
        loadComponent: () => import('./pages/users/users').then((m) => m.Users),
        title: 'Пользователи',
      },
      {
        path: 'notification-subscriptions',
        canMatch: [rbacCanMatch('notifications.manage')],
        loadComponent: () => import('./pages/notification-subscriptions/notification-subscriptions').then((m) => m.NotificationSubscriptions),
        title: 'Подписки на уведомления',
      },
      {
        path: 'notification-log',
        canMatch: [rbacCanMatch('notifications.log.view')],
        loadComponent: () => import('./pages/notification-log/notification-log').then((m) => m.NotificationLog),
        title: 'Журнал уведомлений',
      },
      {
        path: 'catalog-issues',
        canMatch: [rbacCanMatch('catalog.issues.view')],
        loadComponent: () => import('./pages/catalog-issues/catalog-issues').then((m) => m.CatalogIssues),
        title: 'Ошибки настройки каталога',
      },
      {
        path: 'sync',
        canMatch: [catalogIssuesLegacyCanMatch, rbacCanMatch('sync.queue.view')],
        loadComponent: () => import('./pages/sync/sync').then((m) => m.Sync),
        title: 'Синхронизация',
      },
      {
        path: 'integrations/onecfresh',
        canMatch: [rbacCanMatch('integration.onecfresh.manage')],
        loadComponent: () =>
          import('./pages/onecfresh-config/onecfresh-config').then((m) => m.OneCFreshConfig),
        title: '1С Fresh',
      },
      {
        path: 'roles',
        canMatch: [rbacCanMatch('rbac.role.view')],
        loadComponent: () => import('./pages/roles/roles').then((m) => m.Roles),
        title: 'Роли',
      },
      {
        path: 'accounts',
        canMatch: [rbacCanMatch('catalog.account.view')],
        loadComponent: () => import('./pages/accounts/accounts').then((m) => m.Accounts),
        title: 'Счета учета',
      },
      {
        path: 'warehouses',
        canMatch: [rbacCanMatch('catalog.warehouse.view')],
        loadComponent: () => import('./pages/warehouses/warehouses').then((m) => m.Warehouses),
        title: 'Склады',
      },
      {
        path: 'item-types',
        canMatch: [rbacCanMatch('catalog.item_type.view')],
        loadComponent: () => import('./pages/item-types/item-types').then((m) => m.ItemTypes),
        title: 'Вид номенклатуры',
      },
      {
        path: 'price-types',
        canMatch: [rbacCanMatch('catalog.price_type.view')],
        loadComponent: () => import('./pages/price-types/price-types').then((m) => m.PriceTypes),
        title: 'Типы цен',
      },
      {
        path: 'inventory-review',
        canMatch: [rbacAnyCanMatch(['inventory.verify', 'inventory.review.approve'])],
        loadChildren: () => import('./app.inventory.routes').then((m) => m.INVENTORY_ROUTES),
        title: 'Инвентаризация',
      },
      {
        path: 'inventory/labels/:catalogItemId',
        canMatch: [rbacCanMatch('inventory.verify')],
        canActivate: [inventoryLabelListWarehouseGuard],
        loadComponent: () =>
          import('./pages/inventory-label-list/inventory-label-list').then((m) => m.InventoryLabelList),
        title: 'Этикетки номенклатуры',
      },
      {
        path: 'inventory/detail/:labelId',
        canMatch: [rbacAnyCanMatch(['inventory.verify', 'inventory.review.approve'])],
        loadComponent: () =>
          import('./pages/inventory-detail/inventory-detail').then((m) => m.InventoryDetail),
        title: 'Карточка проверки',
      },
      {
        path: 'inventory-locations',
        canMatch: [rbacCanMatch('inventory.location.view')],
        loadComponent: () =>
          import('./pages/inventory-locations/inventory-locations').then((m) => m.InventoryLocations),
        title: 'Адресное хранение',
      },
      {
        path: 'about',
        loadComponent: () => import('./pages/about/about').then((m) => m.About),
        title: 'О приложении',
      },

      {
        path: 'scanner',
        loadComponent: () => import('./pages/scanner/scanner').then((m) => m.Scanner),
      },

      // 404 внутри /app/*
      {
        path: '**',
        loadComponent: () => import('./pages/not-found/not-found').then((m) => m.NotFound),
        title: 'Не найдено'
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // 403 page
  // ---------------------------------------------------------------------------
  {
    path: 'no-access',
    loadComponent: () => import('./pages/no-access/no-access').then((m) => m.NoAccess),
    title: 'Нет доступа',
  },

  // ---------------------------------------------------------------------------
  // Global 404
  // ---------------------------------------------------------------------------
  {
    path: '**',
    loadComponent: () => import('./pages/not-found/not-found').then((m) => m.NotFound),
    title: 'Не найдено',
  },
];
